import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AIActions = () => {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActions();
  }, []);

  const fetchActions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai-actions');
      // Handle both array and object responses
      const actionsData = Array.isArray(response.data) ? response.data : (response.data.actions || []);
      setActions(actionsData);
    } catch (error) {
      console.error('Error fetching AI actions:', error);
      setError('Failed to load AI actions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/ai-actions/new');
  };

  const handleEdit = (action) => {
    navigate(`/ai-actions/${action.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this action?')) return;

    try {
      await api.delete(`/ai-actions/${id}`);
      fetchActions();
    } catch (error) {
      console.error('Error deleting action:', error);
      alert('Failed to delete action');
    }
  };

  const handleToggleActive = async (action) => {
    try {
      await api.put(`/ai-actions/${action.id}`, {
        name: action.name,
        description: action.description,
        action_type: action.action_type,
        target_entity: action.target_entity,
        target_form: action.target_form,
        prompt_template: action.prompt_template || '',
        n8n_webhook_url: action.n8n_webhook_url || '',
        input_fields: action.input_fields || [],
        output_fields: action.output_fields || [],
        is_active: !action.is_active
      });
      fetchActions();
    } catch (error) {
      console.error('Error toggling action:', error);
      alert('Failed to update action');
    }
  };

  const getActionTypeBadge = (type) => {
    const colors = {
      PROMPT: 'badge-primary',
      N8N_WORKFLOW: 'badge-info',
      CREWAI_PROBLEM: 'badge-warning'
    };
    return <span className={`badge ${colors[type] || 'badge-secondary'}`}>
      {type.replace('_', ' ')}
    </span>;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          AI Actions
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #7C3AED'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              ⚡ AI Actions
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Reusable AI-powered transformations for your forms and entities
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>+</span>
            <span>Create Action</span>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Empty State */}
        {actions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: 'var(--gray-500)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚡</div>
            <h3 style={{
              fontSize: '1.5rem',
              color: 'var(--gray-700)',
              marginBottom: '0.5rem'
            }}>
              No AI Actions Yet
            </h3>
            <p style={{
              fontSize: '1rem',
              marginBottom: '2rem',
              color: 'var(--gray-600)'
            }}>
              Create reusable AI-powered transformations that can be applied to any form or entity.
            </p>
            <button onClick={handleCreate} className="btn btn-primary">
              Create Your First Action
            </button>
          </div>
        ) : (
          /* Actions Grid */
          <div style={{
            display: 'grid',
            gap: '1.5rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))'
          }}>
            {actions.map(action => (
              <div key={action.id} style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: 'var(--bg-primary)',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                {/* Card Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: '1rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      color: 'var(--gray-900)',
                      margin: '0 0 0.5rem 0',
                      fontWeight: '600'
                    }}>
                      {action.name}
                    </h3>
                    {getActionTypeBadge(action.action_type)}
                  </div>
                  <span className={`badge ${action.is_active ? 'badge-success' : 'badge-secondary'}`}>
                    {action.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Description */}
                <p style={{
                  color: 'var(--gray-600)',
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  margin: '0 0 1rem 0',
                  minHeight: '3rem'
                }}>
                  {action.description || 'No description provided'}
                </p>

                {/* Meta Info */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  fontSize: '0.875rem',
                  color: 'var(--gray-500)',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--gray-200)',
                  marginBottom: '1rem'
                }}>
                  <span>📋 {action.target_entity}</span>
                  <span>📝 {action.target_form}</span>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(action);
                    }}
                    className="btn btn-sm btn-primary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActive(action);
                    }}
                    className="btn btn-sm btn-outline-secondary"
                  >
                    {action.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(action.id);
                    }}
                    className="btn btn-sm btn-outline-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIActions;
